import { idSchema } from '@/lib/utils'
import { z } from 'zod/v4'
import type { Design, DesignEffectiveSectionOverrideKey, DesignOrigin } from './designs.types'

export const designSchema = z.object({ designId: idSchema('design') })
export const designOriginSchema = z.enum(['custom', 'platform'])
export const DESIGN_ORIGIN_LABELS: Record<DesignOrigin, string> = {
  custom: 'Custom',
  platform: 'Built-in',
}

// Any origin other than `platform`, including one newer than this admin build, reads as Custom.
export function getDesignOriginLabel(
  design: Pick<Design, 'design_origin'> | null | undefined
): string {
  return design?.design_origin === 'platform'
    ? DESIGN_ORIGIN_LABELS.platform
    : DESIGN_ORIGIN_LABELS.custom
}

/** A non-default design must turn one of these on. */
const DESIGN_OVERRIDE_KEYS = [
  'header_banner',
  'header_nav_menu',
  'hero',
  'featured_content',
] satisfies DesignEffectiveSectionOverrideKey[]

export const NON_DEFAULT_OVERRIDE_REQUIRED_ERROR_MESSAGE =
  'Enable at least one override section before saving this design.'
export const DESIGN_SCHEDULE_START_OFFSET_REQUIRED_ERROR_MESSAGE = 'Enter days before holiday.'
export const DESIGN_SCHEDULE_END_OFFSET_REQUIRED_ERROR_MESSAGE = 'Enter days after holiday.'

export const nonDefaultDesignOverridesSchema = z
  .object({
    is_default: z.boolean().optional(),
    section_overrides: z
      .object({
        header_banner: z.boolean().optional(),
        header_brand: z.boolean().optional(),
        header_nav_menu: z.boolean().optional(),
        hero: z.boolean().optional(),
        featured_content: z.boolean().optional(),
      })
      .optional(),
    schedule: z
      .object({
        start_type: z.string().optional(),
        start_offset_days: z.union([z.string(), z.number(), z.null()]).optional(),
        end_type: z.string().optional(),
        end_offset_days: z.union([z.string(), z.number(), z.null()]).optional(),
      })
      .optional(),
  })
  .superRefine((value, ctx) => {
    if (value?.is_default) {
      return
    }

    const overrides = value?.section_overrides ?? {}
    const hasEnabledOverride = DESIGN_OVERRIDE_KEYS.some((key) => overrides?.[key] === true)

    if (!hasEnabledOverride) {
      ctx.addIssue({
        code: 'custom',
        // Keep this as a form-level issue so collapsed override sections do not
        // show field-level error text; UX uses toast guidance instead.
        path: [],
        message: NON_DEFAULT_OVERRIDE_REQUIRED_ERROR_MESSAGE,
      })
    }

    const schedule = value?.schedule
    const isHolidaySchedule = schedule?.start_type === 'holiday_offset'
    const hasHolidayStartOffset =
      schedule?.start_offset_days !== null &&
      schedule?.start_offset_days !== undefined &&
      String(schedule.start_offset_days).trim() !== ''
    const hasHolidayEndOffset =
      schedule?.end_offset_days !== null &&
      schedule?.end_offset_days !== undefined &&
      String(schedule.end_offset_days).trim() !== ''

    if (isHolidaySchedule && !hasHolidayStartOffset) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'start_offset_days'],
        message: DESIGN_SCHEDULE_START_OFFSET_REQUIRED_ERROR_MESSAGE,
      })
    }

    if (isHolidaySchedule && schedule?.end_type === 'holiday_offset' && !hasHolidayEndOffset) {
      ctx.addIssue({
        code: 'custom',
        path: ['schedule', 'end_offset_days'],
        message: DESIGN_SCHEDULE_END_OFFSET_REQUIRED_ERROR_MESSAGE,
      })
    }
  })
